import React, { ReactNode } from 'react'
import moment from 'moment-timezone'
import { dateCommaTimeFormat } from '../utils/general_util'
import WorkerDetail from '../Shifts/Workers/WorkerDetail'
import { Label, Link } from '@workwhile/ui'
import { WorkerStatus } from 'pages/Shifts/ShiftDetail/components/WorkerRow/WorkerStatus'

export const workColumns = (requestWork?: () => void) => [
  {
    dataField: 'id',
    text: 'IDs',
    sort: true,
    headerStyle: { width: '4em', fontSize: 12 },
    formatter: (cell, row) => {
      const lineStyle = { fontSize: 12, margin: 0, lineHeight: 1.4 }
      const labelStyle = { fontWeight: 700 }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={lineStyle}>
            <span style={labelStyle}>Work ID: </span>
            {cell}
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Shift ID: </span>
            <Link to={`/shifts/${row.shift.id}`}>{row.shift.id}</Link>
          </p>
          {row.shift.listings && row.shift.listings.length > 0
            ? row.shift.listings.map((listing) => (
                <p key={listing.id} style={lineStyle}>
                  <span style={labelStyle}>Listing ID: </span>
                  {listing.id}
                </p>
              ))
            : null}
        </div>
      )
    },
  },
  {
    dataField: 'shift',
    text: 'Shift Details',
    sort: true,
    headerStyle: { width: '10em', fontSize: 12 },
    formatter: (cell, row) => {
      const { location } = cell
      const timezone =
        location && location.address
          ? location.address.timezone
          : moment.tz.guess(true)
      const startsAt = moment
        .tz(cell.startsAt, timezone)
        .format(dateCommaTimeFormat)
      const endsAt = moment
        .tz(cell.endsAt, timezone)
        .format(dateCommaTimeFormat)
      const address =
        cell.location && cell.location.address
          ? [
              cell.location.address.street,
              cell.location.address.city,
              cell.location.address.state,
              cell.location.address.zip,
            ]
              .filter(Boolean)
              .join(', ')
          : 'No Address'
      const payLine = cell.payRate
        ? `$${cell.payRate}/hr · total $${cell.pay}`
        : `$${cell.payLumpSum} for the whole shift`

      const lineStyle = { fontSize: 12, margin: 0, lineHeight: 1.4 }
      const labelStyle = { fontWeight: 700 }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={lineStyle}>
            <span style={labelStyle}>Company: </span>
            {cell.company?.name}{' '}
            <Link to={`/companies/${cell.company?.id}`} target="_blank">
              ({cell.company?.id})
            </Link>
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Position: </span>
            {cell.position?.name}{' '}
            <Link
              to={`/position_editor?positionId=${cell.position?.id}`}
              target="_blank"
            >
              ({cell.position?.id})
            </Link>
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Location: </span>
            {address} ({cell.location?.id})
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Pay: </span>
            {payLine}
          </p>
          {row.potentialBonus > 0 ? (
            <p style={lineStyle}>
              <span style={labelStyle}>Bonus: </span>${row.potentialBonus}
            </p>
          ) : null}
          <p style={lineStyle}>
            <span style={labelStyle}>Starts: </span>
            {startsAt}
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Ends: </span>
            {endsAt}
          </p>
        </div>
      )
    },
  },
  {
    dataField: 'status',
    text: 'Status',
    sort: true,
    headerStyle: { width: '5em', fontSize: 12 },
    formatter: (cell, row) => {
      const lineStyle = { fontSize: 12, margin: 0, lineHeight: 1.4 }
      let content: ReactNode | null = null
      if (cell === 'paid') {
        content = <p style={lineStyle}>amount:{row.earnings}</p>
      } else if (cell === 'employer_cancelled' && row.earnings > 0) {
        content = <p style={lineStyle}>Paid amount: {row.earnings}</p>
      }
      return (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 4,
              marginBottom: 4,
            }}
          >
            <WorkerStatus status={cell} minWidth="auto" />
            {row.isReplaceable ? (
              <Label variant="warning" size="small">
                Replaceable
              </Label>
            ) : null}
            {row.replacedAt ? (
              <Label variant="neutral" size="small">
                Replaced
              </Label>
            ) : null}
          </div>
          {content}
          <WorkerDetail
            workId={row.id}
            workerId={row.worker.id}
            shift={row.shift}
            fetchWorkers={requestWork}
            hideWorkerInfo
          />
        </>
      )
    },
  },
  {
    dataField: 'startedAt',
    text: 'Work Info',
    sort: true,
    headerStyle: { width: '5em', fontSize: 12 },
    formatter: (cell, row) => {
      const location = row.shift.location
      const timezone =
        location && location.address
          ? location.address.timezone
          : moment.tz.guess(true)
      const started_at = moment.tz(cell, timezone)
      const completed_at = moment.tz(row.completedAt, timezone)

      const hrsWorked = row.timeWorkedSeconds
        ? Math.max(row.timeWorkedSeconds, 0) / 3600
        : 0

      const lineStyle = { fontSize: 12, margin: 0, lineHeight: 1.4 }
      const labelStyle = { fontWeight: 700 }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={lineStyle}>
            <span style={labelStyle}>Started: </span>
            {started_at.format(dateCommaTimeFormat)}
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Completed: </span>
            {completed_at.format(dateCommaTimeFormat)}
          </p>
          <p style={lineStyle}>
            <span style={labelStyle}>Worked: </span>
            {hrsWorked.toFixed(2)} hrs
          </p>
          {row.reasonEarlyClockout ? (
            <p style={lineStyle}>
              <span style={labelStyle}>Early clockout (worker): </span>
              {row.reasonEarlyClockout}
            </p>
          ) : null}
          {row.supervisorReasonEarlyClockOut ? (
            <p style={lineStyle}>
              <span style={labelStyle}>Early clockout (supervisor): </span>
              {row.supervisorReasonEarlyClockOut}
            </p>
          ) : null}
          {row.trips && row.trips.length > 0 ? (
            <p style={lineStyle}>
              <span style={labelStyle}>Trips: </span>
              {row.trips.map((trip, idx) => (
                <a
                  key={idx}
                  target="_blank"
                  href={trip.embedUrl}
                  rel="noreferrer"
                >
                  location-{idx}
                </a>
              ))}
            </p>
          ) : null}
          {row.isInitiallyReserved ? (
            <p style={lineStyle}>
              <span style={labelStyle}>Reserved: </span>Yes
            </p>
          ) : null}
          {row.shift.supervisors.map((supervisor) => (
            <div key={supervisor.name}>
              <p style={lineStyle}>
                <span style={labelStyle}>Supervisor: </span>
                {supervisor.name}
              </p>
              <p style={lineStyle}>
                <span style={labelStyle}>Phone: </span>
                {supervisor.phoneNumber || 'N/A'}
              </p>
            </div>
          ))}
          <p style={lineStyle}>
            <span style={labelStyle}>Logs: </span>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`https://console.cloud.google.com/logs/query;duration=PT72H;query=%22work_id%3D${row.id}%22?folder=true&organizationId=136924707921&project=workwhile-247820`}
            >
              View Work ID Log
            </a>
          </p>
        </div>
      )
    },
  },
]
